import React from 'react';
import { CreditCard } from '../data/shared/CreditCard';
import { CreditCardDataFiltered } from 'data/shared/CreditCardData';
import { EarnRateInfo } from 'data/types/CardRewardsEarnRates';
import { MobileCardContainer } from './MobileCardContainer';
import { CardContainer } from './CardContainer';

interface MobileRewardsViewProps {
  cards: CreditCard[];
  bestEarnRatesByCard: EarnRateInfo[][];
  handleRemoveCard: (card: CreditCard) => void;
  setShowCardModal: (cardOrState: CreditCard | { card: CreditCard; walletIndex?: number }) => void;
  customizePointAndSpendValues: boolean;
  pointValues: Record<string, number>;
  inUSA: boolean;
  setInUSA: (inUSA: boolean) => void;
  setShowEditPointValues: (show: boolean) => void;
}

export const MobileRewardsView: React.FC<MobileRewardsViewProps> = ({
  cards,
  bestEarnRatesByCard,
  handleRemoveCard,
  setShowCardModal,
  customizePointAndSpendValues,
  pointValues,
  inUSA,
  setInUSA,
  setShowEditPointValues,
}) => {
  // Separate used and unused cards, preserving wallet indices
  const cardsWithIndex = cards.map((card, walletIndex) => ({ card, walletIndex }));
  const usedCards = cardsWithIndex.filter(
    ({ walletIndex }) => bestEarnRatesByCard[walletIndex]?.length > 0
  );
  const unusedCards = cardsWithIndex.filter(
    ({ walletIndex }) => !(bestEarnRatesByCard[walletIndex]?.length > 0)
  );

  const renderCard = (card: CreditCard, isUsed: boolean, walletIndex: number, listIndex: number) => {
    const bestEarnRatesForCard = bestEarnRatesByCard[walletIndex] || [];
    const cardData = CreditCardDataFiltered[card];

    // Skip rendering if cardData is not available
    if (!cardData) {
      return null;
    }

    return (
      <MobileCardContainer
        key={`mobile-${isUsed ? 'card' : 'unused'}-${card}-${listIndex}`}
        card={card}
        cardData={cardData}
        isUsed={isUsed}
        bestEarnRatesForCard={bestEarnRatesForCard}
        onCardClick={() => setShowCardModal({ card, walletIndex })}
        onRemoveCard={() => handleRemoveCard(card)}
      />
    );
  };

  return (
    <div style={{}}>
      {/* U.S. Purchase Toggle */}
      <CardContainer style={{ padding: '10px 16px 16px', marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              fontWeight: '400',
              fontFamily: 'Merriweather',
            }}
          >
            Purchasing in U.S.:
          </div>
          <button
            onClick={() => setInUSA(!inUSA)}
            style={{
              color: '#4DA9E6',
              fontWeight: '400',
              background: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '6px',
            }}
          >
            {inUSA ? '🇺🇸 Yes' : '🌍 No'}
          </button>
        </div>

        {/* Point Values Customization Button */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              fontWeight: '400',
              fontFamily: 'Merriweather',
            }}
          >
            Point values:
          </div>
          <button
            onClick={() => setShowEditPointValues(true)}
            style={{
              color: '#4DA9E6',
              fontSize: '16px',
              fontWeight: '400',
              background: 'none',
              border: 'none',
            }}
          >
            Advanced settings
          </button>
        </div>
      </CardContainer>

      {/* Render used cards first */}
      {usedCards.map(({ card, walletIndex }, index) => renderCard(card, true, walletIndex, index))}

      {/* Render unused cards at the bottom */}
      {unusedCards.map(({ card, walletIndex }, index) => renderCard(card, false, walletIndex, index))}

      {/* Mobile Totals Section */}
      <CardContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              fontWeight: '400',
              fontFamily: 'Merriweather',
            }}
          >
            Cards carried in wallet
          </div>
          <div
            style={{
              fontSize: '18px',
              fontWeight: '400',
              fontFamily: 'Merriweather',
            }}
          >
            {usedCards.length} / {cards.length}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              fontWeight: '400',
              fontFamily: 'Merriweather',
            }}
          >
            Total annual fees
          </div>
          <div
            style={{
              fontSize: '18px',
              fontWeight: '400',
              fontFamily: 'Merriweather',
            }}
          >
            $
            {cards.reduce(
              (total, card) =>
                total +
                (CreditCardDataFiltered[card]?.fees
                  ?.annual_fee_amount_dollars || 0),
              0
            )}
          </div>
        </div>
      </CardContainer>
    </div>
  );
};
